/* eslint-disable no-param-reassign */
import {
  FlowNodeEntity, FreeLayoutPluginContext, WorkflowEdgeJSON, WorkflowNodeJSON, WorkflowNodeLinesData, WorkflowNodePortsData, delay,
  useClientContext,
} from '@flowgram.ai/free-layout-editor';
import { FlowNodeTypeEnum, FlowPartTypeToNodeTypeMap } from '$/domains/node/type';
import { McpCategoriesType } from '$/domains/mcp/type';
import useFlowStore, { flowMtstatTrack } from '$/store/useFlowStore';
import { mtstatTrack } from '$/utils/mtstat';
import { getUrlSearchParams } from '$/utils';
import { useLanguageStore } from '$/store/languageStore';
import { InputMediaNode, SubmitMediaItem } from '$/domains/community/type';
import { communitySubmit } from '$/domains/community/communityService';
import useSopStore from '$/store/useSopStore';
import {
  getStartEditNode, getExecuteWorkflowList, mergeWorkflowList, EditNode,
  MEDIA_TYPE_LIST,
} from './workflowList';

/**
 * 过滤编辑节点的游离态
 *
 * @param {FlowNodeEntity[]} nodeList
 * @return {*}
 */
function filterDissociateEditNode(nodeList: FlowNodeEntity[]) {
  return nodeList.filter((node) => {
    const { data } = node.toJSON();
    const linesData = node.getData(WorkflowNodeLinesData);
    if (EditNode.includes(node.flowNodeType as FlowNodeTypeEnum)) {
      return data?.childrenNodeList?.length || linesData.inputNodes.length;
    }
    return true;
  });
}

/**
 * 判断是否符合“多开始节点可发布”规则
 *
 * 规则说明：
 * 1）每个开始节点都必须有输入节点
 * 2）任意两个开始节点必须存在至少一个公共输入节点
 * 3）所有开始节点经由这些公共输入节点形成连通关系
 *    - 场景1：所有开始节点共享同一个公共节点
 *    - 场景2：不存在单一公共节点，但通过多个公共节点链式连通
 *
 * @param {FreeLayoutPluginContext} ctx 画布上下文
 * @param {FlowNodeEntity[]} allNodes 全部节点
 * @returns {boolean} 是否满足多开始节点发布条件
 */
export function isMultiFlowPublish(ctx: FreeLayoutPluginContext, allNodes: FlowNodeEntity[]) {
  const filterNode = filterDissociateEditNode(allNodes);
  const startNodeList = getStartEditNode(ctx, filterNode, { inPublish: true });
  if (!Array.isArray(startNodeList) || startNodeList.length < 2) {
    return false;
  }

  // 为每个开始节点收集输入节点 id 集合
  const startInputSets = startNodeList.map((node) => {
    const linesData = node.getData(WorkflowNodeLinesData);
    const inputNodes = linesData.inputNodes || [];
    return new Set(inputNodes.map((n) => n.id).filter(Boolean));
  });

  // 如果有开始节点没有任何输入，无法形成公共节点
  if (startInputSets.some((set) => set.size === 0)) {
    return false;
  }

  const startCount = startInputSets.length;
  const adjacency: Array<Set<number>> = Array.from({ length: startCount }, () => new Set());

  const hasIntersection = (a: Set<string>, b: Set<string>) => {
    for (const id of a) {
      if (b.has(id)) {
        return true;
      }
    }
    return false;
  };

  // 构建开始节点之间的公共输入节点图
  for (let i = 0; i < startCount; i += 1) {
    for (let j = i + 1; j < startCount; j += 1) {
      if (hasIntersection(startInputSets[i], startInputSets[j])) {
        adjacency[i].add(j);
        adjacency[j].add(i);
      }
    }
  }

  // 每个开始节点都必须与至少一个其他开始节点有公共输入节点
  if (adjacency.some((set) => set.size === 0)) {
    return false;
  }

  // 检查公共节点关系图是否连通，覆盖两种场景（单一公共节点或链式多个公共节点）
  const visited = new Set<number>();
  const stack = [0];

  while (stack.length) {
    const current = stack.pop() as number;
    if (!visited.has(current)) {
      visited.add(current);
      adjacency[current].forEach((next) => {
        if (!visited.has(next)) {
          stack.push(next);
        }
      });
    }
  }

  return visited.size === startCount;
}

/**
 * 获取全部节点的执行数组
 *
 * @param {FreeLayoutPluginContext} ctx
 * @param {FlowNodeEntity[]} allNodes
 * @return {*}
 */
export function getWorkflowList(ctx: FreeLayoutPluginContext, allNodes: FlowNodeEntity[]) {
  const filterNode = filterDissociateEditNode(allNodes);
  const startNodeList = getStartEditNode(ctx, filterNode, { inPublish: true });
  let executeWorkflowList = getExecuteWorkflowList(ctx, startNodeList, filterNode, 'first', { inPublish: true });
  executeWorkflowList = mergeWorkflowList(ctx, executeWorkflowList, filterNode, { inPublish: true });
  return executeWorkflowList;
}

/**
 * 获取发布按钮禁用状态
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @return {*}
 */
export async function getPublishDisableStatus(ctx: FreeLayoutPluginContext) {
  await delay(100);
  const allNodes: FlowNodeEntity[] = ctx.document.getAllNodes();
  const executeWorkflowList = getWorkflowList(ctx, allNodes);

  const roboneo_web_need_complete_workflow = useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.roboneo_web_need_complete_workflow);
  const roboneo_web_need_all_executed = useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.roboneo_web_need_all_executed);
  //  没有没执行节点流，或者可执行节点流大于1且不符合多流发布逻辑，则禁用发布
  if (!executeWorkflowList.length || (executeWorkflowList.length > 1 && !isMultiFlowPublish(ctx, allNodes))) {
    return {
      disabledPublish: true,
      disabledPublishMessage: roboneo_web_need_complete_workflow,
    };
  }

  //   过滤掉 所有的游离的节点
  const filterAllNodes = filterDissociateEditNode(allNodes).filter((n) => {
    const { data } = n.toJSON();
    const linesData = n.getData(WorkflowNodeLinesData);
    if (EditNode.includes(n.flowNodeType as FlowNodeTypeEnum)) {
      return true;
    }
    return linesData.outputNodes.length || data?.parentNodeId;
  });
  //   遍历所有节点，查看是否满足发布条件
  for (const node of filterAllNodes) {
    switch (node.flowNodeType) {
      // 每一个编辑节点都必须要有输出
      case FlowNodeTypeEnum.IMAGE_EDIT_NODE:
      case FlowNodeTypeEnum.TEXT_EDIT_NODE:
      case FlowNodeTypeEnum.VIDEO_EDIT_NODE:
      case FlowNodeTypeEnum.GIF_EDIT_NODE:
      case FlowNodeTypeEnum.AUDIO_EDIT_NODE: {
        const { data } = node.toJSON();
        if (!data?.childrenNodeList) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_all_executed,
          };
        }
        // 过滤掉pre节点
        const filterChildren: FlowNodeEntity[] = data.childrenNodeList.filter((id: string) => {
          const _n = ctx.document.getNode(id);
          if (_n) {
            const { data: _data } = _n.toJSON();
            return !_data?.isPre;
          }
          return false;
        });
        if (!filterChildren.length) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_all_executed,
          };
        }

        // 检查输入节点是否完整
        const linesData = node.getData(WorkflowNodeLinesData);
        const mcpInfo = useFlowStore.getState().mcpConfigList.find((item) => item.apiName === data?.apiName);
        const checkInputPort = mcpInfo?.inputPortConfig.every((config) => {
          if (config.minNum) {
            const prevNodes = linesData.inputNodes.filter((n) => n.flowNodeType === FlowPartTypeToNodeTypeMap.get(config.sourcePortType));
            return prevNodes.length >= config.minNum;
          }
          return true;
        });

        if (!mcpInfo || !checkInputPort) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_all_executed,
          };
        }

        break;
      }

      //   图片节点是否有值
      case FlowNodeTypeEnum.IMAGE_NODE:
      case FlowNodeTypeEnum.GIF_NODE: {
        const { data } = node.toJSON();
        if (!data?.imageList) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        const imageNotValue = data.imageList.some(({ originUrl, url }: { originUrl: string; url: string; }) => !(originUrl || url));
        if (imageNotValue) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        break;
      }

      //   视频节点
      case FlowNodeTypeEnum.VIDEO_NODE: {
        const { data } = node.toJSON();
        if (!data?.videoList) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        const videoNotValue = data.videoList.some(({ originUrl, url }: { originUrl: string; url: string; }) => !(originUrl || url));
        if (videoNotValue) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        break;
      }

      //   文本节点 必须有值
      case FlowNodeTypeEnum.TEXT_NODE: {
        const { data } = node.toJSON();
        if (!data?.textList) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        const textNotValue = data.textList.some(({ value }: { value: string; }) => !value?.trim());
        if (textNotValue) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        break;
      }
      //   音频节点
      case FlowNodeTypeEnum.AUDIO_NODE: {
        const { data } = node.toJSON();
        if (!data?.audioList) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        const audioNotValue = data.audioList.some(({ originUrl }: { originUrl: string; }) => !originUrl);
        if (audioNotValue) {
          return {
            disabledPublish: true,
            disabledPublishMessage: roboneo_web_need_complete_workflow,
          };
        }
        break;
      }

      default:
        break;
    }
  }

  // 埋点
  if (useFlowStore.getState().disabledPublish) {
    const searchParams = getUrlSearchParams();
    const room_id = searchParams.get('room_id') || '';
    const input_node_num = allNodes.reduce((t, node) => {
      const { data } = node.toJSON();
      return t + (data?.parentNodeId ? 0 : 1);
    }, 0);
    const output_node_num = allNodes.reduce((t, node) => {
      const { data } = node.toJSON();
      return t + ((data?.parentNodeId && !data?.isPre) ? 1 : 0);
    }, 0);
    mtstatTrack('roboneo_can_publish_show', {
      room_id,
      input_node_num,
      output_node_num,
    });
  }

  return {
    disabledPublish: false,
    disabledPublishMessage: roboneo_web_need_complete_workflow,
  };
}

function singleWorkListOutPut(ctx: FreeLayoutPluginContext, workList: (FlowNodeEntity | FlowNodeEntity[])[]): SubmitMediaItem[] {
  if (workList?.length) {
    const lastEditNode = Array.isArray(workList[workList.length - 1])
      ? [...(workList[workList.length - 1] as FlowNodeEntity[])]
      : [workList[workList.length - 1] as FlowNodeEntity];
    return lastEditNode.map((node: FlowNodeEntity) => {
      const childrenNodeList = node.toJSON().data?.childrenNodeList;
      if (childrenNodeList?.length) {
        return childrenNodeList.map((id: string) => {
          const n = ctx.document.getNode(id);
          const _d = n?.toJSON().data;
          // 过滤掉pre节点
          if (n && _d && !_d.isPre) {
            if (n.flowNodeType === FlowNodeTypeEnum.GIF_NODE
              || n.flowNodeType === FlowNodeTypeEnum.IMAGE_NODE) {
              return {
                url: _d?.imageList[0].originUrl || _d?.imageList[0].url || '',
                type: 'image',
              };
            }
            if (n.flowNodeType === FlowNodeTypeEnum.VIDEO_NODE) {
              return {
                url: _d?.videoList[0].originUrl || _d?.videoList[0].url || '',
                type: 'video',
              };
            }
            if (n.flowNodeType === FlowNodeTypeEnum.AUDIO_NODE) {
              return {
                url: _d?.audioList[0].originUrl || _d?.audioList[0].url || '',
                type: 'audio',
              };
            }
            return null;
          }
          return null;
        });
      }
      return null;
    })
      .flat().filter(Boolean);
  }
  return [];
}

/**
 * 获取最后的编辑节点的输出节点
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @return {*}
 */
export function getLastEditNodeOutput(ctx: FreeLayoutPluginContext) {
  const workGroupList = getWorkflowList(ctx, ctx.document.getAllNodes());
  const isMulti = isMultiFlowPublish(ctx, ctx.document.getAllNodes());
  if (isMulti) {
    return workGroupList.reduce((total: SubmitMediaItem[], workList: (FlowNodeEntity | FlowNodeEntity[])[]) => {
      return [...total, ...singleWorkListOutPut(ctx, workList)];
    }, []);
  }
  const workList = workGroupList[0];

  return singleWorkListOutPut(ctx, workList);
}

/**
 * 发布按钮点击埋点
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @param {string} location
 */
export function mtstatPublishClick(ctx: FreeLayoutPluginContext, location: string) {
  const allNodes: FlowNodeEntity[] = ctx.document.getAllNodes();
  const input_node_num = allNodes.reduce((t, node) => {
    const { data } = node.toJSON();
    return t + (data?.parentNodeId ? 0 : 1);
  }, 0);
  const output_node_num = allNodes.reduce((t, node) => {
    const { data } = node.toJSON();
    return t + ((data?.parentNodeId && !data?.isPre) ? 1 : 0);
  }, 0);
  flowMtstatTrack('roboneo_publish_icon_click', {
    input_node_num,
    output_node_num,
    location,
  });
}

/**
 * 在树形结构的 mcpCategoriesList 中递归查找指定 id 的分类
 *
 * @param {McpCategoriesType[]} categories - MCP 分类列表
 * @param {string} id - 要查找的 id
 * @return {*} 找到的分类对象，如果未找到则返回 undefined
 */
export function findMcpCategoryById(categories: McpCategoriesType[], id: string): McpCategoriesType | undefined {
  for (const category of categories) {
    // 如果当前层级找到匹配的 id，直接返回
    if (category.id === id) {
      return category;
    }
    // 如果当前层级没找到，且存在 children，递归查找子层级
    if (category.children && category.children.length > 0) {
      const found = findMcpCategoryById(category.children, id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

/**
 * 获取执行工作流列表对应的 MCP 名称列表
 *
 * 该方法将 executeWorkflowList 中的 FlowNodeEntity 转换为对应的 MCP 名称和输出信息，
 * 并保持与原数组相同的嵌套结构。支持在树形结构的 mcpCategoriesList 中递归查找
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx - 画布上下文
 * @param {(FlowNodeEntity | FlowNodeEntity[])[][]} executeWorkflowList - 执行工作流列表，三层嵌套结构
 * @return {*} 返回与 executeWorkflowList 结构一致的对象数组，包含 name 和 output 字段
 *
 * @example
 * // 输入结构：[[[node1, node2], node3], [node4]]
 * // 输出结构：[[[{name: '文生图', output: ['url1']}, {name: '图片编辑', output: []}], {name: '视频生成', output: ['url2']}], [{name: '音频生成', output: []}]]
 */
export function getMcpNameExecuteList(ctx: FreeLayoutPluginContext, executeWorkflowList: (FlowNodeEntity | FlowNodeEntity[])[][]) {
  const { mcpCategoriesList, mcpConfigList } = useFlowStore.getState();
  const { languageStore } = useLanguageStore.getState();
  
  // 获取所有的 edges
  const allEdges = ctx.document.toJSON().edges;

  // 获取编辑节点的输出 URL 列表
  const getNodeOutputUrls = (node: FlowNodeEntity): string[] => {
    const { data } = node.toJSON();
    const childrenNodeList = data?.childrenNodeList;

    if (!childrenNodeList || childrenNodeList.length === 0) {
      return [];
    }

    return childrenNodeList
      .map((id: string) => {
        const childNode = ctx.document.getNode(id);
        if (!childNode) return null;

        const childData = childNode.toJSON().data;
        // 过滤掉 pre 节点
        if (childData?.isPre) return null;

        // 根据节点类型获取对应的 URL
        if (childNode.flowNodeType === FlowNodeTypeEnum.IMAGE_NODE
            || childNode.flowNodeType === FlowNodeTypeEnum.GIF_NODE) {
          return childData?.imageList?.[0]?.originUrl || childData?.imageList?.[0]?.url || null;
        }

        if (childNode.flowNodeType === FlowNodeTypeEnum.VIDEO_NODE) {
          return childData?.videoList?.[0]?.originUrl || childData?.videoList?.[0]?.url || null;
        }

        if (childNode.flowNodeType === FlowNodeTypeEnum.AUDIO_NODE) {
          return childData?.audioList?.[0]?.originUrl || childData?.audioList?.[0]?.url || null;
        }

        return null;
      })
      .filter(Boolean) as string[];
  };

  // 根据 port_name 从实际端口中获取对应的值
  const getPortValues = (node: FlowNodeEntity, portNames: string[]): Record<string, string | string[]> => {
    const nodeJson = node.toJSON();
    const { id, data } = nodeJson;
    const inputPorts = node.getData(WorkflowNodePortsData).inputPorts;
    const mcpConfig = mcpConfigList.find((mcp) => mcp.apiName === data?.apiName);
    const portValues: Record<string, string | string[]> = {};

    if (!inputPorts || !mcpConfig || !data) {
      return portValues;
    }

    // 遍历所有输入端口，查找匹配的 port_name
    inputPorts.forEach((port) => {
      const portTargetEle = port.targetElement;
      // 字段名
      const portName = portTargetEle?.getAttribute('data-port-name') ?? '';
      
      // 只处理在 portNames 列表中的端口
      if (!portNames.includes(portName)) {
        return;
      }

      const portConfig = mcpConfig.inputPortConfig.find((item) => item.port_name === portName);
      if (!portConfig) return;

      const att_type = portConfig.att_type;

      // 端口id
      const portId = portTargetEle?.getAttribute('data-port-id');
      // 基于端口id查找上个节点id
      const sourceNodeID = allEdges.find((ed) => ed.targetPortID === portId)?.sourceNodeID;
      
      if (sourceNodeID) {
        const sourceNode = ctx.document.getNode(sourceNodeID);
        if (!sourceNode) return;

        const sourceNodeData = sourceNode.toJSON().data;
        const sourceNodeType = sourceNode.flowNodeType;
        
        // 如果 sourceNode 是工具节点（有 apiName），过滤掉不处理
        if (sourceNodeData?.apiName) {
          return;
        }

        // 如果 sourceNode 是输入节点（图片、视频、文本等），直接从 data 中取值
        let value: string | null = null;

        if (sourceNodeType === FlowNodeTypeEnum.TEXT_NODE) {
          value = sourceNodeData?.textList?.[0]?.value || null;
        } else if (sourceNodeType === FlowNodeTypeEnum.IMAGE_NODE || sourceNodeType === FlowNodeTypeEnum.GIF_NODE) {
          value = sourceNodeData?.imageList?.[0]?.originUrl || sourceNodeData?.imageList?.[0]?.url || null;
        } else if (sourceNodeType === FlowNodeTypeEnum.VIDEO_NODE) {
          value = sourceNodeData?.videoList?.[0]?.originUrl || sourceNodeData?.videoList?.[0]?.url || null;
        } else if (sourceNodeType === FlowNodeTypeEnum.AUDIO_NODE) {
          value = sourceNodeData?.audioList?.[0]?.originUrl || sourceNodeData?.audioList?.[0]?.url || null;
        }

        if (value) {
          if (!portValues[portName]) {
            portValues[portName] = att_type === 'array' ? [] : '';
          }
          if (att_type === 'array' && Array.isArray(portValues[portName])) {
            portValues[portName].push(value);
          }
          if (att_type === 'string') {
            portValues[portName] = value;
          }
        }
      }
    });

    return portValues;
  };

  // 遍历executeWorkflowList，保持其结构，将FlowNodeEntity转换为对应的 name 和 output
  return executeWorkflowList.map((workflowGroup) => {
    return workflowGroup.map((workflowItem) => {
      // 如果是数组，遍历数组中的每个节点
      if (Array.isArray(workflowItem)) {
        return workflowItem.map((node) => {
          const { data } = node.toJSON();
          // 根据节点的 data.mcpCategoriesId 在 mcpCategoriesList 中递归查找对应的 name
          const mcpCategory = findMcpCategoryById(mcpCategoriesList, data?.mcpCategoriesId);
          const name = mcpCategory?.name ? languageStore.smartcatTranslationText((v) => v[mcpCategory.name as keyof typeof v]) : '';
          const output = getNodeOutputUrls(node);
          const mcpConfig = mcpConfigList.find((item) => item.apiName === data?.apiName);

          // 从 inputPortConfig 中提取 port_name 字段
          const portNames = mcpConfig?.inputPortConfig?.map((item) => item.port_name) || [];
          // 基于 port_name 从实际端口中获取对应的值
          const portValues = getPortValues(node, portNames);

          return {
            name,
            output,
            apiName: data?.apiName,
            input: {
              ...data?.parameters,
              ...portValues,
            },
          };
        }).filter((resultItem) => resultItem.name); // 过滤掉 name 为空的项
      }
      // 如果是单个节点，直接获取其对应的 name 和 output
      const { data } = workflowItem.toJSON();
      const mcpCategory = findMcpCategoryById(mcpCategoriesList, data?.mcpCategoriesId);
      const name = mcpCategory?.name ? languageStore.smartcatTranslationText((v) => v[mcpCategory.name as keyof typeof v]) : '';
      const output = getNodeOutputUrls(workflowItem);
      const mcpConfig = mcpConfigList.find((item) => item.apiName === data?.apiName);
      // 从 inputPortConfig 中提取 port_name 字段
      const portNames = mcpConfig?.inputPortConfig?.map((item) => item.port_name) || [];
      // 基于 port_name 从实际端口中获取对应的值
      const portValues = getPortValues(workflowItem, portNames);

      return {
        name,
        output,
        apiName: data?.apiName,
        input: {
          ...data?.parameters,
          ...portValues,
        },
      };
    });
  });
}

interface publishCommunityParams {
  name: string;
  description: string;
  cover_media: SubmitMediaItem[];
  example_media: SubmitMediaItem[];
}

export function usePublishCommunity() {
  const ctx = useClientContext();

  // 获取 编辑节点 输入节点对应的
  const getParametersApiName = (nodeJson: WorkflowNodeJSON, edges: WorkflowEdgeJSON[]) => {
    const { id, data } = nodeJson;
    const _params: Record<string, string | string[]> = {};
    const node = ctx.document.getNode(id);
    const inputPorts = node?.getData(WorkflowNodePortsData).inputPorts;
    const { mcpConfigList } = useFlowStore.getState();
    const mcpConfig = mcpConfigList.find((mcp) => mcp.apiName === data.apiName);

    if (inputPorts && mcpConfigList.length) {
      inputPorts.forEach((port) => {
        const portTargetEle = port.targetElement;
        // 字段名
        const portName = portTargetEle?.getAttribute('data-port-name') ?? '';
        const att_type = mcpConfig?.inputPortConfig.find((item) => item.port_name === portName)?.att_type;

        // 端口id
        const portId = portTargetEle?.getAttribute('data-port-id');
        // 基于端口id查找上个节点id
        const sourceNodeID = edges.find((ed) => ed.targetPortID === portId)?.sourceNodeID;
        if (sourceNodeID) {
          // 获取上个节点的父节点
          const parentNodeId = ctx.document.getNode(sourceNodeID)?.toJSON().data?.parentNodeId;
          const apiName = ctx.document.getNode(parentNodeId)?.toJSON().data?.apiName;
          if (apiName) {
            if (!_params[portName]) {
              _params[portName] = att_type === 'array' ? [] : '';
            }
            if (att_type === 'array' && Array.isArray(_params[portName])) {
              _params[portName].push(`{${apiName}}`);
            }
            if (att_type === 'string') {
              _params[portName] = `{${apiName}}`;
            }
          }
        }
      });
    }

    return _params;
  };

  // 发布时，获取当前编辑节点的上一个编辑节点
  const getInputNodeList = (id: string) => {
    const node = ctx.document.getNode(id);

    if (node) {
      const linesData = node.getData(WorkflowNodeLinesData);
      return linesData.inputNodes
        .map((prevNode) => {
          const { data } = prevNode.toJSON();
          // 编辑节点
          if (EditNode.includes(prevNode.flowNodeType as FlowNodeTypeEnum)) {
            return prevNode.id;
          }
          if (MEDIA_TYPE_LIST.includes(prevNode.flowNodeType as FlowNodeTypeEnum) && data?.parentNodeId) {
            return data.parentNodeId;
          }
          return null;
        })
        .filter(Boolean) as string[];
    }
    return [];
  };

  const getFlowJSONData = () => {
    const allNodes = ctx.document.toJSON();
    const { nodes, edges } = allNodes;

    // 过滤掉pre节点和游离节点
    const filterNodes = nodes.filter((nodeJson) => {
      const { data, type, id } = nodeJson;

      const hasEdges = edges.some((edge) => edge.sourceNodeID === id);
      const freeMediaNode = MEDIA_TYPE_LIST.includes(type as FlowNodeTypeEnum) && !data.parentNodeId && !hasEdges;
      const freeEditNode = EditNode.includes(type as FlowNodeTypeEnum) && !data.childrenNodeList?.length && !hasEdges;
      return !data?.isPre && !freeMediaNode && !freeEditNode;
    });
    filterNodes.forEach((nodeJson) => {
      const { data, type, id } = nodeJson;
      if (EditNode.includes(type as FlowNodeTypeEnum)) {
        const inputNodeList = getInputNodeList(id);

        if (inputNodeList.length) {
          const _params = getParametersApiName(nodeJson, edges);
          nodeJson.data.parameters = {
            ...data.parameters,
            ..._params,
          };
        }

        nodeJson.data.inputNodeList = inputNodeList;
      }
    });

    // 如果存在生成的子节点链接到下一个是编辑节点，则重写此连线为编辑节点连编辑节点，方便社区详情页面里服务端执行
    let caseEdges = edges.map((edge) => {
      const sourceNode = ctx.document.getNode(edge.sourceNodeID);
      const targetNode = ctx.document.getNode(edge.targetNodeID);
      const sourceNodeData = sourceNode?.toJSON().data;
      if (MEDIA_TYPE_LIST.includes(sourceNode?.flowNodeType as FlowNodeTypeEnum)
        && sourceNodeData?.parentNodeId
      && EditNode.includes(targetNode?.flowNodeType as FlowNodeTypeEnum)) {
        // edge.sourceNodeID = sourceNodeData?.parentNodeId;
        return {
          ...edge,
          sourceNodeID: sourceNodeData?.parentNodeId,
        };
      }
      return {
        ...edge,
      };
    });

    // 给每一条边加上 目标端口的name字段，方便服务端执行
    caseEdges = caseEdges.map((edge) => {
      const targetNode = ctx.document.getNode(edge.targetNodeID);
      const inputPorts = targetNode?.getData(WorkflowNodePortsData).inputPorts;
      let targetPortName = '';
      inputPorts?.forEach((port) => {
        const portTargetEle = port.targetElement;
        // 端口id
        const portId = portTargetEle?.getAttribute('data-port-id');
        if (portId === edge.targetPortID) {
          targetPortName = portTargetEle?.getAttribute('data-port-name') ?? '';
        }
      });
      return {
        targetPortName,
        ...edge,
      };
    });
    allNodes.nodes = filterNodes;
    allNodes.edges = edges;
    return { allNodes, caseEdges };
  };

  // 获取开始节点的所有输入节点，便于在社区详情也展示和更新媒体数据
  const getStartNodeInputMediaNodes = (startNodeList: FlowNodeEntity[]) => {
    const inputMediaNode = startNodeList
      .map((node) => {
        const linesData = node.getData(WorkflowNodeLinesData);
        const { inputNodes } = linesData;
        return inputNodes;
      })
      .flat()
      .map((node) => {
        const { data } = node.toJSON();
        return {
          id: node.id,
          type: node.flowNodeType,
          data,
        } as InputMediaNode;
      });

    // 根据 node.id 去重
    const uniqueInputMediaNode = Array.from(
      new Map(inputMediaNode.map((node) => [node.id, node])).values(),
    );

    return uniqueInputMediaNode;
  };

  const publishCommunity = async ({
    name,
    description,
    cover_media,
    example_media,
  }: publishCommunityParams) => {
    try {
      const allNodes: FlowNodeEntity[] = ctx.document.getAllNodes();
      const room_id = useFlowStore.getState().roomId;
      // 过滤async async pre节点
      const filterPreNodes = allNodes.filter((node) => {
        const { data } = node.toJSON();
        return !data?.isPre;
      });
      const startNodeList = getStartEditNode(ctx, filterPreNodes, { inPublish: true });
      const executeWorkflowList = getWorkflowList(ctx, filterPreNodes);

      if (executeWorkflowList.length) {
        // 获取开始编辑节点的输入节点数据
        const inputMediaNode = getStartNodeInputMediaNodes(startNodeList);
        const allJSONData = getFlowJSONData();
        const mcpNameExecuteList = getMcpNameExecuteList(ctx, executeWorkflowList);
        console.log('inputMediaNode', inputMediaNode);
        console.log('allJSONData', allJSONData);
        console.log('mcpNameExecuteList', mcpNameExecuteList);
        const submitParams = {
          room_id,
          canvas_data: JSON.stringify(allJSONData.allNodes),
          data: JSON.stringify({
            caseEdges: allJSONData.caseEdges,
            inputMediaNode,
            mcpNameExecuteList,
          }),
          name,
          description,
          cover_media,
          example_media,
        };
        let result = null;
        if (useSopStore.getState().fromAdmin) {
          result = submitParams;
        } else {
          result = await communitySubmit(submitParams);
        }
        return result;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  return publishCommunity;
}
